import React, { useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { inject, observer } from 'mobx-react';
import TailwindScope from '../Shared/TailwindScope';
import { callApi } from '../../util/apiWrapper';
import '../../tailwind.css';

interface ImpersonationAuthStore {
  handleImpersonatedLogin: (response: unknown) => void;
}

interface SeniorStoreShape {
  resetState?: () => void;
}

interface StartImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
  authStore: ImpersonationAuthStore;
  SeniorStore?: SeniorStoreShape;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const StartImpersonationDialogBase = ({ open, onClose, authStore, SeniorStore: seniorStore }: StartImpersonationDialogProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isEmailValid = useMemo(() => EMAIL_REGEX.test(email.trim()), [email]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setEmail('');
    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!isEmailValid) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const response = await callApi(authStore, '/user/impersonation', 'POST', { email: email.trim() });
      authStore.handleImpersonatedLogin(response);
      seniorStore?.resetState?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error && error.message?.includes('404')) {
        setErrorMessage('Active user not found.');
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Unable to start impersonation.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="start-impersonation-title">
      <DialogTitle id="start-impersonation-title">Impersonate User</DialogTitle>
      <DialogContent>
        <TailwindScope className="pt-2">
          <div className="flex flex-col gap-3">
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label="User email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={Boolean(errorMessage)}
              helperText={errorMessage}
              disabled={isSubmitting}
              inputProps={{ 'aria-label': 'Impersonated user email' }}
            />
          </div>
        </TailwindScope>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={isSubmitting || !isEmailValid}>
          {isSubmitting ? 'Starting...' : 'Start Impersonation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StartImpersonationDialog = inject('SeniorStore')(observer(StartImpersonationDialogBase));

export default StartImpersonationDialog;
